import { useParams, useNavigate } from 'react-router-dom';
import { useEffect, useState, useCallback } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { useProducts } from '@/hooks/useProducts';
import { useStats } from '@/hooks/useStats';
import { useScrollReveal } from '@/hooks/useScrollReveal';
import { Product } from '@/mocks/products';

/* ─── Lightbox ─── */
interface LightboxProps {
  images: string[];
  startIndex: number;
  productName: string;
  onClose: () => void;
}

const Lightbox = ({ images, startIndex, productName, onClose }: LightboxProps) => {
  const [idx, setIdx] = useState(startIndex);

  const prev = useCallback(() => setIdx(i => (i - 1 + images.length) % images.length), [images.length]);
  const next = useCallback(() => setIdx(i => (i + 1) % images.length), [images.length]);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft') prev();
      if (e.key === 'ArrowRight') next();
    };
    document.addEventListener('keydown', handler);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handler);
      document.body.style.overflow = '';
    };
  }, [onClose, prev, next]);

  return (
    <div
      className="fixed inset-0 z-[999] flex items-center justify-center"
      style={{ background: 'rgba(0,0,0,0.95)' }}
      onClick={onClose}
    >
      {/* Close */}
      <button
        onClick={onClose}
        className="absolute top-4 right-4 w-10 h-10 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/25 text-white transition-all duration-200 cursor-pointer z-10"
      >
        <i className="ri-close-line text-xl"></i>
      </button>

      {/* Counter */}
      <div className="absolute top-4 left-1/2 -translate-x-1/2 text-white/60 text-sm font-medium">
        {idx + 1} / {images.length}
      </div>

      {/* Prev */}
      {images.length > 1 && (
        <button
          onClick={e => { e.stopPropagation(); prev(); }}
          className="absolute left-4 top-1/2 -translate-y-1/2 w-11 h-11 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/25 text-white transition-all duration-200 cursor-pointer z-10"
        >
          <i className="ri-arrow-left-s-line text-2xl"></i>
        </button>
      )}

      {/* Next */}
      {images.length > 1 && (
        <button
          onClick={e => { e.stopPropagation(); next(); }}
          className="absolute right-4 top-1/2 -translate-y-1/2 w-11 h-11 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/25 text-white transition-all duration-200 cursor-pointer z-10"
        >
          <i className="ri-arrow-right-s-line text-2xl"></i>
        </button>
      )}

      {/* Main image */}
      <div
        className="max-w-5xl max-h-[85vh] w-full mx-16 flex items-center justify-center"
        onClick={e => e.stopPropagation()}
      >
        <img
          src={images[idx]}
          alt={`${productName} - Fotoğraf ${idx + 1}`}
          className="max-w-full max-h-[85vh] object-contain rounded-xl"
          style={{ userSelect: 'none' }}
        />
      </div>

      {/* Thumbnail strip */}
      {images.length > 1 && (
        <div className="absolute bottom-5 left-1/2 -translate-x-1/2 flex gap-2">
          {images.map((img, i) => (
            <button
              key={i}
              onClick={e => { e.stopPropagation(); setIdx(i); }}
              className={`w-12 h-12 rounded-lg overflow-hidden border-2 transition-all duration-200 cursor-pointer flex-shrink-0 ${i === idx ? 'border-orange-500 scale-110' : 'border-white/20 opacity-50 hover:opacity-80'}`}
            >
              <img src={img} alt={`Küçük ${i + 1}`} className="w-full h-full object-cover object-top" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

/* ─── Main Page ─── */
const UrunDetayPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { products } = useProducts();
  const { recordView } = useStats();
  const [product, setProduct] = useState<Product | null>(null);
  const [others, setOthers] = useState<Product[]>([]);
  const [heroVisible, setHeroVisible] = useState(false);
  const [activePhoto, setActivePhoto] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);

  const { ref: othersRef, visible: othersVisible } = useScrollReveal(0.05);
  const { ref: guaranteeRef, visible: guaranteeVisible } = useScrollReveal(0.1);

  useEffect(() => {
    if (!products.length) return;
    const found = products.find(p => p.id === id);
    if (!found) {
      navigate('/ikinci-el');
      return;
    }
    setProduct(found);
    setOthers(products.filter(p => p.id !== id).slice(0, 8));
    recordView(found.id, found.name, found.category);
    const mainIdx = found.mainImageIndex ?? 0;
    setActivePhoto(mainIdx);
    setTimeout(() => setHeroVisible(true), 80);
  }, [id, products, navigate]);

  if (!product) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center">
          <i className="ri-loader-4-line text-4xl text-orange-500 animate-spin block mb-3"></i>
          <p className="text-slate-500 text-sm">Yükleniyor...</p>
        </div>
      </div>
    );
  }

  const productImages = (product.images && product.images.filter(Boolean).length > 0)
    ? product.images.filter(Boolean)
    : [product.image];

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />

      {/* Lightbox */}
      {lightboxOpen && (
        <Lightbox
          images={productImages}
          startIndex={activePhoto}
          productName={product.name}
          onClose={() => setLightboxOpen(false)}
        />
      )}

      {/* Hero */}
      <div className="pt-16 md:pt-20 bg-slate-900 relative overflow-hidden">
        <div className="absolute inset-0 opacity-5 pointer-events-none"
          style={{
            backgroundImage: 'linear-gradient(rgba(255,165,0,0.4) 1px, transparent 1px), linear-gradient(90deg, rgba(255,165,0,0.4) 1px, transparent 1px)',
            backgroundSize: '50px 50px',
          }}
        />
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-10 md:py-14 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">

            {/* Image Gallery */}
            <div className={`flex flex-col gap-3 transition-all duration-700 ${heroVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-10'}`}>
              {/* Main image - clickable for lightbox */}
              <div
                className="relative w-full h-[260px] sm:h-[340px] md:h-[420px] rounded-2xl overflow-hidden bg-slate-700 flex items-center justify-center cursor-zoom-in group"
                onClick={() => setLightboxOpen(true)}
              >
                <img
                  src={productImages[activePhoto]}
                  alt={product.name}
                  className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-500"
                />
                {/* Zoom hint overlay */}
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-all duration-300 flex items-center justify-center">
                  <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-black/50 backdrop-blur-sm text-white px-4 py-2 rounded-full text-sm font-medium flex items-center gap-2">
                    <i className="ri-zoom-in-line"></i>
                    Büyüt
                  </div>
                </div>
                {/* Photo count badge */}
                {productImages.length > 1 && (
                  <div className="absolute top-3 right-3 bg-black/50 backdrop-blur-sm text-white text-xs px-2.5 py-1 rounded-full flex items-center gap-1.5 font-medium">
                    <i className="ri-image-line text-xs"></i>
                    {productImages.length} Fotoğraf
                  </div>
                )}
              </div>

              {/* Thumbnails */}
              {productImages.length > 1 && (
                <div className="flex gap-2">
                  {productImages.map((img, i) => (
                    <button
                      key={i}
                      onClick={() => setActivePhoto(i)}
                      className={`w-16 h-16 rounded-lg overflow-hidden border-2 transition-all duration-200 cursor-pointer flex-shrink-0 ${i === activePhoto ? 'border-orange-500 scale-105' : 'border-white/20 opacity-60 hover:opacity-100 hover:border-white/50'}`}
                    >
                      <img src={img} alt={`Foto ${i + 1}`} className="w-full h-full object-cover object-top" />
                    </button>
                  ))}
                  {/* Lightbox open button */}
                  <button
                    onClick={() => setLightboxOpen(true)}
                    className="w-16 h-16 rounded-lg border-2 border-white/20 hover:border-orange-400 bg-white/5 hover:bg-white/10 flex flex-col items-center justify-center gap-1 text-white/60 hover:text-orange-400 transition-all duration-200 cursor-pointer flex-shrink-0"
                    title="Tüm fotoğrafları gör"
                  >
                    <i className="ri-fullscreen-line text-lg"></i>
                    <span className="text-xs">Büyüt</span>
                  </button>
                </div>
              )}
            </div>

            {/* Info */}
            <div className={`transition-all duration-700 delay-150 ${heroVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-10'}`}>
              <div className="flex items-center gap-2 mb-3">
                <button
                  onClick={() => navigate(-1)}
                  className="text-slate-400 hover:text-white text-sm flex items-center gap-1 cursor-pointer transition-colors"
                >
                  <i className="ri-arrow-left-line"></i>
                  Geri
                </button>
                <span className="text-slate-600 text-sm">/</span>
                <span className="text-orange-400 text-sm font-medium">{product.category}</span>
              </div>

              <h1 className="text-2xl md:text-4xl font-extrabold text-white mb-4 leading-tight">
                {product.name}
              </h1>

              <p className="text-slate-300 text-sm md:text-base leading-relaxed mb-6">
                {product.description}
              </p>

              <div className={`flex items-baseline gap-2 mb-8 transition-all duration-700 delay-300 ${heroVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
                <span className="text-4xl md:text-5xl font-extrabold text-orange-400">
                  {product.price.toLocaleString('tr-TR')}
                </span>
                <span className="text-2xl font-bold text-orange-400/70">₺</span>
              </div>

              <div className={`flex flex-col sm:flex-row gap-3 transition-all duration-700 delay-400 ${heroVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
                <a
                  href={`https://wa.me/905325416344?text=Merhaba, ${encodeURIComponent(product.name)} ürünü hakkında bilgi almak istiyorum. Fiyat: ${product.price.toLocaleString('tr-TR')} ₺`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex-1 bg-green-600 hover:bg-green-700 hover:scale-105 text-white py-3.5 rounded-xl font-bold text-sm transition-all duration-200 flex items-center justify-center gap-2 cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-whatsapp-line text-lg"></i>
                  WhatsApp ile Sor
                </a>
                <a
                  href="tel:+905325416344"
                  className="flex-1 bg-orange-500 hover:bg-orange-600 hover:scale-105 text-white py-3.5 rounded-xl font-bold text-sm transition-all duration-200 flex items-center justify-center gap-2 cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-phone-line text-lg"></i>
                  Hemen Ara
                </a>
                <a
                  href="/iletisim"
                  className="flex-1 border border-white/30 hover:border-white/60 hover:bg-white/10 text-white py-3.5 rounded-xl font-bold text-sm transition-all duration-200 flex items-center justify-center gap-2 cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-mail-line"></i>
                  Mesaj Gönder
                </a>
              </div>

              <div
                ref={guaranteeRef}
                className={`mt-6 bg-white/5 border border-white/10 rounded-xl p-4 flex items-start gap-3 transition-all duration-700 delay-500 ${heroVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}
              >
                <i className="ri-shield-check-line text-orange-400 text-lg mt-0.5"></i>
                <div>
                  <p className="text-white text-sm font-semibold">Güvenli Alışveriş</p>
                  <p className="text-slate-400 text-xs mt-0.5">Tüm ürünler test edilmiş ve garantilidir. Sorun yaşarsanız teknik destek sağlanır.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Other Products */}
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-14">
        <div
          className={`flex items-center justify-between mb-8 transition-all duration-700 ${othersVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
          ref={othersRef}
        >
          <div>
            <h2 className="text-2xl font-extrabold text-slate-900">Diğer Ürünler</h2>
            <p className="text-slate-500 text-sm mt-1">Diğer ikinci el ürünlerimize göz atın</p>
          </div>
          <a
            href="/ikinci-el"
            className="text-orange-500 hover:text-orange-600 text-sm font-semibold flex items-center gap-1 cursor-pointer whitespace-nowrap"
          >
            Tümünü Gör
            <i className="ri-arrow-right-line"></i>
          </a>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {others.map((p, i) => (
            <div
              key={p.id}
              onClick={() => navigate(`/urun/${p.id}`)}
              className={`bg-white rounded-xl border border-slate-100 overflow-hidden hover:-translate-y-2 hover:scale-[1.02] transition-all duration-500 cursor-pointer group ${othersVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
              style={{ transitionDelay: othersVisible ? `${i * 70}ms` : '0ms' }}
            >
              <div className="w-full h-44 overflow-hidden bg-slate-100 relative">
                <img
                  src={p.image}
                  alt={p.name}
                  className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                <span className="absolute top-2 left-2 bg-orange-500 text-white text-xs font-semibold px-2 py-0.5 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  {p.category}
                </span>
              </div>
              <div className="p-4">
                <span className="text-xs text-orange-500 font-semibold uppercase tracking-wide">{p.category}</span>
                <h3 className="text-slate-900 font-bold text-sm mt-1 mb-2 line-clamp-2 leading-snug">{p.name}</h3>
                <p className="text-slate-500 text-xs line-clamp-2 mb-3">{p.description}</p>
                <div className="flex items-center justify-between">
                  <span className="text-orange-500 font-extrabold text-base">{p.price.toLocaleString('tr-TR')} ₺</span>
                  <span className="text-xs bg-slate-900 group-hover:bg-orange-500 text-white px-3 py-1.5 rounded-lg transition-colors duration-300 flex items-center gap-1 whitespace-nowrap">
                    <i className="ri-arrow-right-line"></i> İncele
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default UrunDetayPage;
